import {getConversionFactors} from './conversion-factors.fn';
import {getFeatureUnitConfig} from './feature-unit-config.fn';
import {FeatureType, StorageUnit, TimeUnit, FeatureKey} from './feature-constants.enum';

const CF = getConversionFactors();
const featureUnitMap = getFeatureUnitConfig();

/**
 * Format a base-unit feature value for display.
 * Single source of truth for all feature value display formatting.
 *
 * @param value  Raw value in base units (bytes, seconds, count, or string)
 * @param featureKey  Feature key from featureUnitConfig (e.g. 'storage_space')
 * @returns Human-readable string with appropriate unit
 */
export function formatFeatureValue(value: any, featureKey?: string, labelResolver?: (key: string) => string): string {
	if (value === null || value === undefined || value === '') return 'N/A';

	if (featureKey === FeatureKey.VideoCodecs) {
		return formatVideoCodecs(String(value));
	}
	if (featureKey === FeatureKey.HdrSupport) {
		return formatBoolean(value, labelResolver);
	}

	const num = parseFloat(String(value));

	// Negative values represent unlimited
	if (!isNaN(num) && num < 0) return 'Unlimited';
	if (!isNaN(num) && num === 0) return '0';

	// Non-numeric strings (resolution "1080p") — return as-is
	if (isNaN(num)) return String(value);

	if (!featureKey) return compactNumber(Math.round(num));

	const config = featureUnitMap[featureKey];
	if (!config) return compactNumber(Math.round(num));

	switch (config.type) {
		case FeatureType.Storage: return formatStorage(num, labelResolver);
		case FeatureType.Time:    return featureKey === FeatureKey.EncodingMinutes
			? formatMinutes(num, labelResolver)
			: formatTime(num);
		case FeatureType.Count:   return formatCount(num, featureKey, labelResolver);
		case FeatureType.String:  return String(value);
		case FeatureType.Boolean: return formatBoolean(value, labelResolver);
		default:                  return String(value);
	}
}

/**
 * Convert a display value + unit to base units (bytes for storage, seconds for time).
 * Single source of truth for all display→base conversions.
 */
export function toBaseUnit(value: number, unit: string): number {
	const factor = CF[unit] ?? 1;
	return Math.round(value * factor);
}

/**
 * Convert a base-unit value to a display value using a fixed unit.
 * Use when the original unit must be preserved (e.g. single→group migration).
 */
export function fromBaseUnitFixed(base: number, unit: string): string {
	const factor = CF[unit] ?? 1;
	return String(+(( base / factor).toFixed(2)));
}

/**
 * Convert a base-unit value to a display value with auto-selected unit.
 * Used to pre-populate form inputs when editing existing values.
 */
export function fromBaseUnit(
	value: number,
	type: FeatureType
): {displayValue: string; unit: string} {
	if (type === FeatureType.Storage) {
		if (value >= CF[StorageUnit.TB]) return {displayValue: trimDecimals(value / CF[StorageUnit.TB]), unit: StorageUnit.TB};
		if (value >= CF[StorageUnit.GB]) return {displayValue: trimDecimals(value / CF[StorageUnit.GB]), unit: StorageUnit.GB};
		return {displayValue: trimDecimals(value / CF[StorageUnit.MB]), unit: StorageUnit.MB};
	}
	if (type === FeatureType.Time) {
		if (value >= CF[TimeUnit.Hours] && value % CF[TimeUnit.Hours] === 0)
			return {displayValue: String(value / CF[TimeUnit.Hours]), unit: TimeUnit.Hours};
		if (value >= CF[TimeUnit.Minutes])
			return {displayValue: String(value / CF[TimeUnit.Minutes]), unit: TimeUnit.Minutes};
		return {displayValue: String(value), unit: TimeUnit.Seconds};
	}
	return {displayValue: String(value), unit: ''};
}

// ─── private helpers ────────────────────────────────────────────────────────

function trimDecimals(n: number): string {
	return String(+(n.toFixed(2)));
}

function formatVideoCodecs(value: string): string {
	const labels: {[key: string]: string} = {
		h264: 'H.264',
		h265: 'H.265',
		H264: 'H.264',
		H265: 'H.265',
		'H.264': 'H.264',
		'H.265': 'H.265',
	};

	return value
		.split(',')
		.map(codec => codec.trim())
		.filter(codec => codec)
		.map(codec => labels[codec] ?? codec)
		.join(', ');
}

function formatStorage(bytes: number, labelResolver?: (key: string) => string): string {
	const u = (key: string) => labelResolver?.(key) ?? key;
	if (bytes >= CF[StorageUnit.TB]) return `${trimDecimals(bytes / CF[StorageUnit.TB])} ${u(StorageUnit.TB)}`;
	if (bytes >= CF[StorageUnit.GB]) return `${trimDecimals(bytes / CF[StorageUnit.GB])} ${u(StorageUnit.GB)}`;
	if (bytes >= CF[StorageUnit.MB]) return `${trimDecimals(bytes / CF[StorageUnit.MB])} ${u(StorageUnit.MB)}`;
	if (bytes >= CF[StorageUnit.KB]) return `${trimDecimals(bytes / CF[StorageUnit.KB])} ${u(StorageUnit.KB)}`;
	return `${bytes} ${u(StorageUnit.B)}`;
}

function formatMinutes(seconds: number, labelResolver?: (key: string) => string): string {
	if (seconds < 60) return `${Math.round(seconds)}s`;
	const mins = Math.floor(seconds / 60);
	const label = labelResolver?.(mins !== 1 ? TimeUnit.Minutes : TimeUnit.Minute) ?? 'min';
	return `${compactNumber(mins)} ${label}`;
}

function formatTime(seconds: number): string {
	const s = Math.round(seconds);
	if (s >= 86400) {
		const days = Math.floor(s / 86400);
		const hours = Math.floor((s % 86400) / 3600);
		return hours > 0 ? `${days}d ${hours}h` : `${days} day${days !== 1 ? 's' : ''}`;
	}
	if (s >= 3600) {
		const hours = Math.floor(s / 3600);
		const mins = Math.floor((s % 3600) / 60);
		return mins > 0 ? `${hours}h ${mins}m` : `${hours} hour${hours !== 1 ? 's' : ''}`;
	}
	if (s >= 60) {
		const mins = Math.floor(s / 60);
		const secs = s % 60;
		return secs > 0 ? `${mins}m ${secs}s` : `${mins} minute${mins !== 1 ? 's' : ''}`;
	}
	return `${s} second${s !== 1 ? 's' : ''}`;
}

function formatCount(value: number, featureKey: string, labelResolver?: (key: string) => string): string {
	const rounded = Math.round(value);
	if (featureKey === FeatureKey.VideoUploads) {
		const singular = labelResolver?.('VIDEO') ?? 'video';
		const plural = labelResolver?.('VIDEOS') ?? 'videos';
		return `${compactNumber(rounded)} ${rounded !== 1 ? plural : singular}`;
	}
	if (featureKey === FeatureKey.MaxEncodesPerVideo || featureKey === 'encodes') {
		const singular = labelResolver?.('ENCODE') ?? 'encode';
		const plural = labelResolver?.('ENCODES') ?? 'encodes';
		return `${compactNumber(rounded)} ${rounded !== 1 ? plural : singular}`;
	}
	return compactNumber(rounded);
}

function compactNumber(n: number): string {
	if (n >= 1_000_000) return `${trimDecimals(n / 1_000_000)}M`;
	return String(n);
}

function formatBoolean(value: any, labelResolver?: (key: string) => string): string {
	return value === 'true'
		? labelResolver?.('STATUS.ACTIVATED') ?? ''
		: labelResolver?.('STATUS.DISACTIVATED') ?? '';
}

/**
 * Format bytes to human readable string (1 decimal place).
 * Shared utility — replaces all duplicate private formatBytes methods.
 */
export function formatBytes(bytes: number): string {
	if (bytes === 0) return `0 ${StorageUnit.B}`;
	const k = 1024;
	const sizes = [StorageUnit.B, StorageUnit.KB, StorageUnit.MB, StorageUnit.GB, StorageUnit.TB];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Format seconds to human readable string (e.g. 90s → "1m 30s", 3720s → "1h 2m 10s").
 * Shared utility — replaces all duplicate private formatDuration methods.
 */
export function formatDuration(seconds: number, includeSeconds = false): string {
	if (seconds < 60) return `${seconds}s`;
	if (seconds < 3600) {
		const m = Math.floor(seconds / 60);
		const s = seconds % 60;
		return includeSeconds && s > 0 ? `${m}m ${s}s` : `${m}m`;
	}
	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	const secs = seconds % 60;
	let result = minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
	if (includeSeconds && secs > 0) result += ` ${secs}s`;
	return result;
}

/**
 * @deprecated Use formatFeatureValue() instead.
 * Kept for backward compatibility.
 */
export const formatUnitValue = (
	value: number,
	type: FeatureType,
	rawValue?: string
): string => {
	if (type === FeatureType.String) return rawValue || 'N/A';
	return formatFeatureValue(value);
};
